import React, { createContext, useContext, useEffect, useState } from 'react';
import { tr, HTML_LANG } from '../utils/i18n';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const translations = {
  fr: /* i18n:extract */ {
    // Navigation
    services: 'Services',
    benefits: 'Avantages',
    testimonials: 'Témoignages',
    integrations: 'Intégrations',
    contact: 'Contact',
    requestDemo: 'Demander une démo',
    
    // Hero Section
    heroTitle: 'Maximisez votre performance',
    heroSubtitle: 'avec la technologie Deep Link OptimHub',
    heroDescription: 'OptimHub est une plateforme DSP spécialisée dans la technologie de deep link qui connecte les marchands avec des consommateurs hautement engagés. En tant que traffic brokers, nous achetons du trafic au CPC et vous ne payez qu\'au CPA (coût par action/vente) - vous ne payez que pour les conversions réelles.',
    startNow: 'Commencer maintenant',
    requestDemoBtn: 'Demander une démo',
    ourPremiumClients: 'Nos clients premium',
    
    // Stats
    deepLinksGenerated: 'Deep Links générés mensuellement',
    conversionRate: 'Taux de conversion moyen',
    premiumPartners: 'Partenaires premium',
    trustingClients: 'Clients nous faisant confiance',
    
    // Services
    servicesTitle: 'Solutions DSP',
    servicesSubtitle: 'pour maximiser votre ROI',
    servicesDescription: 'Découvrez notre gamme complète de solutions publicitaires programmatiques conçues pour transformer vos campagnes digitales et optimiser vos performances.',
    customSolution: 'Besoin d\'une solution sur mesure ?',
    contactUs: 'Contactez-nous',
    
    // Service Items
    msnTitle: 'MSN Microsoft Network',
    msnDescription: 'Accédez à l\'écosystème Microsoft avec des placements sur Outlook, Bing Search, Edge Shopping et MSN Start. Touchez des audiences qualifiées sur des plateformes de confiance mondiale.',
    
    couponTitle: 'Coupon & Deals',
    couponDescription: 'Placez votre marque sur les sites de coupons et promotions fréquentés par des consommateurs en phase finale d\'achat. Idéal pour capturer l\'intention d\'achat élevée.',
    
    commerceTitle: 'Commerce Content',
    commerceDescription: 'Intégrez vos publicités dans du contenu contextuel pertinent sur des blogs, sites d\'actualités et plateformes de contenu pour une expérience utilisateur optimale.',
    
    bnplTitle: 'Buy Now Pay Later (BNPL)',
    bnplDescription: 'Atteignez des consommateurs avec un pouvoir d\'achat élevé sur les plateformes de financement comme Klarna, Afterpay, et ZIP. Parfait pour des AOV élevés.',
    
    autocompleteTitle: 'Browser Autocomplete',
    autocompleteDescription: 'Captez l\'intention de recherche en temps réel avec des suggestions intelligentes dans l\'autocomplétion des navigateurs. Maximisez la visibilité au moment clé.',
    
    deepLinkTitle: 'Deep Link Technology',
    deepLinkDescription: 'Technologie propriétaire OptimHub pour monétiser les deep links et connecter directement les consommateurs aux pages produits les plus pertinentes.',
    
    // Benefits
    benefitsTitle: 'Pourquoi choisir',
    benefitsSubtitle: 'OptimHub ?',
    benefitsDescription: 'Découvrez les avantages uniques qui font d\'OptimHub la plateforme DSP de référence pour les annonceurs les plus exigeants. Notre modèle de traffic broker vous garantit un ROI optimal.',
    readyToTransform: 'Prêt à transformer vos campagnes publicitaires ?',
    benefitsCta: 'Rejoignez plus de 500 entreprises qui utilisent OptimHub pour maximiser leur ROI publicitaire et atteindre leurs objectifs de croissance.',
    
    // New CPC to CPA benefits
    cpcToCpaTitle: 'Modèle CPC vers CPA',
    cpcToCpaDescription: 'Nous achetons le trafic au CPC et vous ne payez qu\'au CPA - zéro risque, maximum de résultats',
    
    trafficBrokerTitle: 'Traffic Brokers Experts',
    trafficBrokerDescription: 'Notre expertise en tant que courtiers en trafic garantit l\'optimisation continue de vos campagnes',
    
    payOnlyResultsTitle: 'Payez uniquement les résultats',
    payOnlyResultsDescription: 'Modèle révolutionnaire : vous ne payez que pour les conversions réelles, pas pour les clics',
    
    // Testimonials
    testimonialsTitle: 'Ce que disent',
    testimonialsSubtitle: 'nos clients',
    testimonialsDescription: 'Découvrez comment OptimHub a transformé les stratégies publicitaires de nos clients et les a aidés à atteindre leurs objectifs.',
    moreThanClients: 'Plus de 10000 clients nous font confiance',
    techPartners: 'Nos partenaires technologiques',
    
    // Contact
    contactTitle: 'Prêt à commencer ?',
    contactSubtitle: 'Contactez-nous',
    contactDescription: 'Notre équipe d\'experts vous accompagne dans la mise en place de vos campagnes DSP pour maximiser votre ROI publicitaire.',
    stayConnected: 'Restons en contact',
    contactInfo: 'Nous sommes là pour répondre à toutes vos questions et vous accompagner dans le développement de vos stratégies publicitaires.',
    email: 'Email',
    address: 'Adresse',
    guaranteedResponse: 'Réponse garantie',
    responseTime: 'Notre équipe vous répond sous 24h maximum pour discuter de vos projets.',
    personalizedDemo: 'Demander une démo personnalisée',
    
    // Form
    firstName: 'Prénom',
    lastName: 'Nom',
    professionalEmail: 'Email professionnel',
    company: 'Entreprise',
    role: 'Fonction',
    monthlyBudget: 'Budget publicitaire mensuel',
    message: 'Message',
    selectBudget: 'Sélectionnez votre budget',
    under10k: 'Moins de 10 000€',
    '10k-50k': '10 000€ - 50 000€',
    '50k-100k': '50 000€ - 100 000€',
    '100k-500k': '100 000€ - 500 000€',
    over500k: 'Plus de 500 000€',
    sendRequest: 'Envoyer la demande',
    sending: 'Envoi en cours...',
    privacyNotice: 'En soumettant ce formulaire, vous acceptez notre politique de confidentialité. Vos données sont traitées en conformité avec le RGPD.',
    
    // Footer
    footerDescription: 'La plateforme DSP de nouvelle génération qui maximise vos performances publicitaires grâce à l\'intelligence artificielle et des technologies de pointe.',
    enterprise: 'Entreprise',
    about: 'À propos',
    careers: 'Carrières',
    blog: 'Blog',
    partners: 'Partenaires',
    privacyPolicy: 'Politique de confidentialité',
    termsOfService: 'Conditions d\'utilisation',
    gdpr: 'RGPD',
    allRightsReserved: 'Tous droits réservés.',
    
    // Success messages
    successMessage: 'Votre demande a été envoyée avec succès ! Notre équipe vous contactera sous 24h.',
    
    // Placeholders
    yourFirstName: 'Votre prénom',
    yourLastName: 'Votre nom',
    yourEmail: 'votre.email@entreprise.com',
    yourCompany: 'Nom de votre entreprise',
    yourRole: 'Votre fonction',
    projectDescription: 'Décrivez votre projet ou vos objectifs publicitaires...'
  } /* i18n:end */,
  
  en: {
    // Navigation
    services: 'Services',
    benefits: 'Benefits',
    testimonials: 'Testimonials',
    integrations: 'Integrations',
    contact: 'Contact',
    requestDemo: 'Request Demo',
    
    // Hero Section
    heroTitle: 'Maximize your performance',
    heroSubtitle: 'with OptimHub Deep Link Technology',
    heroDescription: 'OptimHub is a DSP platform specialized in deep link technology that connects merchants with highly engaged consumers. As traffic brokers, we buy traffic at CPC and you only pay at CPA (cost per action/sale) - you only pay for real conversions.',
    startNow: 'Start now',
    requestDemoBtn: 'Request a demo',
    ourPremiumClients: 'Our premium clients',
    
    // Stats
    deepLinksGenerated: 'Deep Links generated monthly',
    conversionRate: 'Average conversion rate',
    premiumPartners: 'Premium partners',
    trustingClients: 'Clients trusting us',
    
    // Services
    servicesTitle: 'DSP Solutions',
    servicesSubtitle: 'to maximize your ROI',
    servicesDescription: 'Discover our complete range of programmatic advertising solutions designed to transform your digital campaigns and optimize your performance.',
    customSolution: 'Need a custom solution?',
    contactUs: 'Contact us',
    
    // Service Items
    msnTitle: 'MSN Microsoft Network',
    msnDescription: 'Access the Microsoft ecosystem with placements on Outlook, Bing Search, Edge Shopping and MSN Start. Reach qualified audiences on globally trusted platforms.',
    
    couponTitle: 'Coupon & Deals',
    couponDescription: 'Place your brand on coupon and promotion sites frequented by consumers in final purchase phase. Ideal for capturing high purchase intent.',
    
    commerceTitle: 'Commerce Content',
    commerceDescription: 'Integrate your ads into relevant contextual content on blogs, news sites and content platforms for optimal user experience.',
    
    bnplTitle: 'Buy Now Pay Later (BNPL)',
    bnplDescription: 'Reach consumers with high purchasing power on financing platforms like Klarna, Afterpay, and ZIP. Perfect for high AOV.',
    
    autocompleteTitle: 'Browser Autocomplete',
    autocompleteDescription: 'Capture search intent in real-time with intelligent suggestions in browser autocomplete. Maximize visibility at the key moment.',
    
    deepLinkTitle: 'Deep Link Technology',
    deepLinkDescription: 'OptimHub proprietary technology to monetize deep links and directly connect consumers to the most relevant product pages.',
    
    // Benefits
    benefitsTitle: 'Why choose',
    benefitsSubtitle: 'OptimHub?',
    benefitsDescription: 'Discover the unique advantages that make OptimHub the reference DSP platform for the most demanding advertisers. Our traffic broker model guarantees optimal ROI.',
    readyToTransform: 'Ready to transform your advertising campaigns?',
    benefitsCta: 'Join more than 500 companies using OptimHub to maximize their advertising ROI and achieve their growth objectives.',
    
    // New CPC to CPA benefits
    cpcToCpaTitle: 'CPC to CPA Model',
    cpcToCpaDescription: 'We buy traffic at CPC and you only pay at CPA - zero risk, maximum results',
    
    trafficBrokerTitle: 'Expert Traffic Brokers',
    trafficBrokerDescription: 'Our expertise as traffic brokers ensures continuous optimization of your campaigns',
    
    payOnlyResultsTitle: 'Pay only for results',
    payOnlyResultsDescription: 'Revolutionary model: you only pay for real conversions, not for clicks',
    
    // Testimonials
    testimonialsTitle: 'What our',
    testimonialsSubtitle: 'clients say',
    testimonialsDescription: 'Discover how OptimHub has transformed our clients\' advertising strategies and helped them achieve their objectives.',
    moreThanClients: 'More than 10000 clients trust us',
    techPartners: 'Our technology partners',
    
    // Contact
    contactTitle: 'Ready to start?',
    contactSubtitle: 'Contact us',
    contactDescription: 'Our team of experts accompanies you in setting up your DSP campaigns to maximize your advertising ROI.',
    stayConnected: 'Let\'s stay connected',
    contactInfo: 'We are here to answer all your questions and accompany you in developing your advertising strategies.',
    email: 'Email',
    address: 'Address',
    guaranteedResponse: 'Guaranteed response',
    responseTime: 'Our team responds within 24h maximum to discuss your projects.',
    personalizedDemo: 'Request a personalized demo',
    
    // Form
    firstName: 'First name',
    lastName: 'Last name',
    professionalEmail: 'Professional email',
    company: 'Company',
    role: 'Role',
    monthlyBudget: 'Monthly advertising budget',
    message: 'Message',
    selectBudget: 'Select your budget',
    under10k: 'Under $10,000',
    '10k-50k': '$10,000 - $50,000',
    '50k-100k': '$50,000 - $100,000',
    '100k-500k': '$100,000 - $500,000',
    over500k: 'Over $500,000',
    sendRequest: 'Send request',
    sending: 'Sending...',
    privacyNotice: 'By submitting this form, you accept our privacy policy. Your data is processed in compliance with GDPR.',
    
    // Footer
    footerDescription: 'The new generation DSP platform that maximizes your advertising performance through artificial intelligence and cutting-edge technologies.',
    enterprise: 'Company',
    about: 'About',
    careers: 'Careers',
    blog: 'Blog',
    partners: 'Partners',
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
    gdpr: 'GDPR',
    allRightsReserved: 'All rights reserved.',
    
    // Success messages
    successMessage: 'Your request has been sent successfully! Our team will contact you within 24h.',
    
    // Placeholders
    yourFirstName: 'Your first name',
    yourLastName: 'Your last name',
    yourEmail: 'your.email@company.com',
    yourCompany: 'Your company name',
    yourRole: 'Your role',
    projectDescription: 'Describe your project or advertising objectives...'
  },
  
  de: {
    // Navigation
    services: 'Leistungen',
    benefits: 'Vorteile',
    testimonials: 'Referenzen',
    integrations: 'Integrationen',
    contact: 'Kontakt',
    requestDemo: 'Demo anfordern',
    
    // Hero Section
    heroTitle: 'Maximieren Sie Ihre Performance',
    heroSubtitle: 'mit OptimHub Deep Link Technologie',
    heroDescription: 'OptimHub ist eine DSP-Plattform, die sich auf Deep-Link-Technologie spezialisiert hat und Händler mit hochengagierten Verbrauchern verbindet. Als Traffic-Broker kaufen wir Traffic zum CPC und Sie zahlen nur zum CPA (Kosten pro Aktion/Verkauf) - Sie zahlen nur für echte Conversions.',
    startNow: 'Jetzt starten',
    requestDemoBtn: 'Demo anfordern',
    ourPremiumClients: 'Unsere Premium-Kunden',
    
    // Stats
    deepLinksGenerated: 'Monatlich generierte Deep Links',
    conversionRate: 'Durchschnittliche Conversion-Rate',
    premiumPartners: 'Premium-Partner',
    trustingClients: 'Vertrauende Kunden',
    
    // Services
    servicesTitle: 'DSP-Lösungen',
    servicesSubtitle: 'zur Maximierung Ihres ROI',
    servicesDescription: 'Entdecken Sie unser komplettes Angebot an programmatischen Werbelösungen, die darauf ausgelegt sind, Ihre digitalen Kampagnen zu transformieren und Ihre Performance zu optimieren.',
    customSolution: 'Benötigen Sie eine maßgeschneiderte Lösung?',
    contactUs: 'Kontaktieren Sie uns',
    
    // Service Items
    msnTitle: 'MSN Microsoft Network',
    msnDescription: 'Zugang zum Microsoft-Ökosystem mit Platzierungen auf Outlook, Bing Search, Edge Shopping und MSN Start. Erreichen Sie qualifizierte Zielgruppen auf global vertrauenswürdigen Plattformen.',
    
    couponTitle: 'Coupon & Deals',
    couponDescription: 'Platzieren Sie Ihre Marke auf Coupon- und Promotion-Sites, die von Verbrauchern in der finalen Kaufphase besucht werden. Ideal zum Erfassen hoher Kaufabsicht.',
    
    commerceTitle: 'Commerce Content',
    commerceDescription: 'Integrieren Sie Ihre Anzeigen in relevanten kontextuellen Content auf Blogs, News-Sites und Content-Plattformen für optimale Nutzererfahrung.',
    
    bnplTitle: 'Buy Now Pay Later (BNPL)',
    bnplDescription: 'Erreichen Sie Verbraucher mit hoher Kaufkraft auf Finanzierungsplattformen wie Klarna, Afterpay und ZIP. Perfekt für hohe AOV.',
    
    autocompleteTitle: 'Browser Autocomplete',
    autocompleteDescription: 'Erfassen Sie Suchabsicht in Echtzeit mit intelligenten Vorschlägen in Browser-Autocomplete. Maximieren Sie Sichtbarkeit im entscheidenden Moment.',
    
    deepLinkTitle: 'Deep Link Technologie',
    deepLinkDescription: 'OptimHub proprietäre Technologie zur Monetarisierung von Deep Links und direkten Verbindung von Verbrauchern zu den relevantesten Produktseiten.',
    
    // Benefits
    benefitsTitle: 'Warum',
    benefitsSubtitle: 'OptimHub wählen?',
    benefitsDescription: 'Entdecken Sie die einzigartigen Vorteile, die OptimHub zur Referenz-DSP-Plattform für die anspruchsvollsten Werbetreibenden machen. Unser Traffic-Broker-Modell garantiert optimalen ROI.',
    readyToTransform: 'Bereit, Ihre Werbekampagnen zu transformieren?',
    benefitsCta: 'Schließen Sie sich mehr als 500 Unternehmen an, die OptimHub nutzen, um ihren Werbe-ROI zu maximieren und ihre Wachstumsziele zu erreichen.',
    
    // New CPC to CPA benefits
    cpcToCpaTitle: 'CPC zu CPA Modell',
    cpcToCpaDescription: 'Wir kaufen Traffic zum CPC und Sie zahlen nur zum CPA - null Risiko, maximale Ergebnisse',
    
    trafficBrokerTitle: 'Experte Traffic-Broker',
    trafficBrokerDescription: 'Unsere Expertise als Traffic-Broker gewährleistet kontinuierliche Optimierung Ihrer Kampagnen',
    
    payOnlyResultsTitle: 'Nur für Ergebnisse zahlen',
    payOnlyResultsDescription: 'Revolutionäres Modell: Sie zahlen nur für echte Conversions, nicht für Klicks',
    
    // Testimonials
    testimonialsTitle: 'Was unsere',
    testimonialsSubtitle: 'Kunden sagen',
    testimonialsDescription: 'Entdecken Sie, wie OptimHub die Werbestrategien unserer Kunden transformiert und ihnen geholfen hat, ihre Ziele zu erreichen.',
    moreThanClients: 'Mehr als 10000 Kunden vertrauen uns',
    techPartners: 'Unsere Technologie-Partner',
    
    // Contact
    contactTitle: 'Bereit zu starten?',
    contactSubtitle: 'Kontaktieren Sie uns',
    contactDescription: 'Unser Expertenteam begleitet Sie bei der Einrichtung Ihrer DSP-Kampagnen zur Maximierung Ihres Werbe-ROI.',
    stayConnected: 'Bleiben wir in Kontakt',
    contactInfo: 'Wir sind hier, um alle Ihre Fragen zu beantworten und Sie bei der Entwicklung Ihrer Werbestrategien zu begleiten.',
    email: 'E-Mail',
    address: 'Adresse',
    guaranteedResponse: 'Garantierte Antwort',
    responseTime: 'Unser Team antwortet innerhalb von maximal 24h, um Ihre Projekte zu diskutieren.',
    personalizedDemo: 'Personalisierte Demo anfordern',
    
    // Form
    firstName: 'Vorname',
    lastName: 'Nachname',
    professionalEmail: 'Geschäftliche E-Mail',
    company: 'Unternehmen',
    role: 'Position',
    monthlyBudget: 'Monatliches Werbebudget',
    message: 'Nachricht',
    selectBudget: 'Wählen Sie Ihr Budget',
    under10k: 'Unter 10.000€',
    '10k-50k': '10.000€ - 50.000€',
    '50k-100k': '50.000€ - 100.000€',
    '100k-500k': '100.000€ - 500.000€',
    over500k: 'Über 500.000€',
    sendRequest: 'Anfrage senden',
    sending: 'Wird gesendet...',
    privacyNotice: 'Durch das Absenden dieses Formulars akzeptieren Sie unsere Datenschutzrichtlinie. Ihre Daten werden in Übereinstimmung mit der DSGVO verarbeitet.',
    
    // Footer
    footerDescription: 'Die DSP-Plattform der neuen Generation, die Ihre Werbeleistung durch künstliche Intelligenz und modernste Technologien maximiert.',
    enterprise: 'Unternehmen',
    about: 'Über uns',
    careers: 'Karriere',
    blog: 'Blog',
    partners: 'Partner',
    privacyPolicy: 'Datenschutzrichtlinie',
    termsOfService: 'Nutzungsbedingungen',
    gdpr: 'DSGVO',
    allRightsReserved: 'Alle Rechte vorbehalten.',
    
    // Success messages
    successMessage: 'Ihre Anfrage wurde erfolgreich gesendet! Unser Team wird Sie innerhalb von 24h kontaktieren.',
    
    // Placeholders
    yourFirstName: 'Ihr Vorname',
    yourLastName: 'Ihr Nachname',
    yourEmail: 'ihre.email@unternehmen.com',
    yourCompany: 'Ihr Firmenname',
    yourRole: 'Ihre Position',
    projectDescription: 'Beschreiben Sie Ihr Projekt oder Ihre Werbeziele...'
  }
};

const SUPPORTED_LANGUAGES = ['fr', 'en', 'de', 'it', 'es', 'zh'];

// Priority: ?lang= URL parameter > language prefix in the path > visitor's previous choice > browser languages > English
export const detectLanguage = () => {
  const param = new URLSearchParams(window.location.search).get('lang');
  if (param && SUPPORTED_LANGUAGES.includes(param.toLowerCase().split('-')[0])) {
    return param.toLowerCase().split('-')[0];
  }

  const prefix = window.location.pathname.split('/')[1];
  if (SUPPORTED_LANGUAGES.includes(prefix)) return prefix;

  const stored = localStorage.getItem('optimhub-language');
  if (stored && SUPPORTED_LANGUAGES.includes(stored)) return stored;

  const browserLanguages = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const language of browserLanguages) {
    const code = (language || '').toLowerCase().split('-')[0];
    if (SUPPORTED_LANGUAGES.includes(code)) return code;
  }

  return 'en';
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(detectLanguage);

  useEffect(() => {
    document.documentElement.lang = HTML_LANG[currentLanguage] || 'fr';
    localStorage.setItem('optimhub-language', currentLanguage);
  }, [currentLanguage]);

  const switchLanguage = (language) => {
    setCurrentLanguage(language);
  };

  const t = (key) => {
    const dict = translations[currentLanguage];
    if (dict && dict[key]) return dict[key];
    const source = translations.fr[key];
    if (!source) return key;
    const translated = tr(currentLanguage, source);
    return translated === source ? translations.en[key] || source : translated;
  };

  return (
    <LanguageContext.Provider value={{
      currentLanguage,
      switchLanguage,
      t,
      translations: translations[currentLanguage] || translations.fr
    }}>
      {children}
    </LanguageContext.Provider>
  );
};